// Attendance — 오늘(체크인/아웃 대시보드) · 이번 달 · 연장근무 + 각종 신청.
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../shared/api/client";
import { PageHeader } from "../../shared/components/PageHeader";
import { Empty, Loading } from "../../shared/components/Empty";
import { Pill } from "../../shared/components/Pill";
import { toast } from "../../shared/stores/toasts";

// NOTE: "Record"라는 이름은 TS 내장 Record<K,V>를 가려 컴파일 에러를 내므로 AttRecord 사용.
type AttRecord = {
  id: number; user_id: number; work_date: string;
  check_in: string | null; check_out: string | null;
  work_minutes: number; overtime_minutes: number; status: string | null;
};

// 서버는 UTC로 저장 — 표시는 항상 로컬(KST) 변환.
const fmtTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" }) : "—";
const fmtHm = (mins: number) => `${Math.floor(mins / 60)}h ${mins % 60}m`;
const localDate = (d: Date = new Date()) => d.toLocaleDateString("sv-SE"); // YYYY-MM-DD

export default function AttendancePage() {
  const [params] = useSearchParams();
  const filter = params.get("filter") || "today"; // today | month | overtime (subnavs.ts와 일치)

  const [items, setItems] = useState<AttRecord[] | null>(null);
  const [reqOpen, setReqOpen] = useState(false);
  const [extraReq, setExtraReq] = useState<"REMOTE_WORK_REQUEST" | "BUSINESS_TRIP_REQUEST" | "OFF_SITE_REQUEST" | null>(null);

  const reload = () => api.get<AttRecord[]>("/attendance/me").then((r) => setItems(r.data));
  useEffect(() => { reload(); }, []);

  // 오늘 뷰의 실시간 시계/경과 시간
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (filter !== "today") return;
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [filter]);

  const checkIn = async () => { try { await api.post("/attendance/check-in", { method: "WEB" }); toast.success("출근 체크 완료"); reload(); } catch (e: any) { toast.error(e?.response?.data?.error?.message ?? "출근 체크 실패"); } };
  const checkOut = async () => { try { await api.post("/attendance/check-out"); toast.success("퇴근 체크 완료"); reload(); } catch (e: any) { toast.error(e?.response?.data?.error?.message ?? "퇴근 체크 실패"); } };
  const undoCheckOut = async () => {
    if (!confirm("퇴근 체크를 취소하고 다시 근무 중 상태로 되돌릴까요?")) return;
    try { await api.post("/attendance/check-out/undo"); toast.success("퇴근 정정 완료", "다시 근무 중 상태입니다"); reload(); }
    catch (e: any) { toast.error(e?.response?.data?.error?.message ?? "퇴근 정정 실패"); }
  };

  // 오늘 레코드 — work_date는 서버(UTC) 기준일 수 있어 로컬/UTC 둘 다 매칭
  const todayRec = useMemo(() => {
    if (!items) return null;
    const tLocal = localDate();
    const tUtc = new Date().toISOString().slice(0, 10);
    return items.find((r) => r.work_date === tLocal) ?? items.find((r) => r.work_date === tUtc) ?? null;
  }, [items]);

  const workedMins = useMemo(() => {
    if (!todayRec?.check_in) return 0;
    if (todayRec.check_out) return todayRec.work_minutes;
    return Math.max(0, Math.floor((now.getTime() - new Date(todayRec.check_in).getTime()) / 60000));
  }, [todayRec, now]);

  const monthStats = useMemo(() => {
    const arr = items ?? [];
    return {
      days: arr.filter((r) => r.check_in).length,
      work: arr.reduce((s, r) => s + r.work_minutes, 0),
      overtime: arr.reduce((s, r) => s + r.overtime_minutes, 0),
    };
  }, [items]);

  const overtimeRecs = useMemo(() => (items ?? []).filter((r) => r.overtime_minutes > 0), [items]);

  const checkedIn = !!todayRec?.check_in;
  const checkedOut = !!todayRec?.check_out;

  return (
    <>
      <PageHeader title="근태" subtitle="출퇴근 · 연장근무"
        right={<>
          <button className="t-btn t-btn-outline" onClick={() => setReqOpen(true)}>연장근무</button>
          <button className="t-btn t-btn-outline" onClick={() => setExtraReq("REMOTE_WORK_REQUEST")}>재택 신청</button>
          <button className="t-btn t-btn-outline" onClick={() => setExtraReq("BUSINESS_TRIP_REQUEST")}>출장 신청</button>
          <button className="t-btn t-btn-outline" onClick={() => setExtraReq("OFF_SITE_REQUEST")}>외근 신청</button>
        </>}
      />

      {items === null && <Loading/>}

      {/* ── 오늘 ─────────────────────────────────────────────────────────── */}
      {items !== null && filter === "today" && (
        <div style={{ display: "grid", gap: 16, maxWidth: 760 }}>
          <div className="t-card" style={{ padding: 28, textAlign: "center" }}>
            <div style={{ fontSize: 13, color: "var(--text-3)" }}>
              {now.toLocaleDateString("ko-KR", { year: "numeric", month: "long", day: "numeric", weekday: "long" })}
            </div>
            <div style={{ fontSize: 44, fontWeight: 800, letterSpacing: 1, margin: "6px 0 2px", fontVariantNumeric: "tabular-nums" }}>
              {now.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })}
            </div>
            <div style={{ marginBottom: 18 }}>
              {!checkedIn && <Pill kind="muted">미출근</Pill>}
              {checkedIn && !checkedOut && <Pill kind="ok">근무 중</Pill>}
              {checkedOut && <Pill kind="info">퇴근 완료</Pill>}
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button className="t-btn t-btn-primary" style={{ minWidth: 130, height: 44, fontSize: 15 }}
                disabled={checkedIn} onClick={checkIn}>
                {checkedIn ? `출근 ${fmtTime(todayRec!.check_in)}` : "출근 체크"}
              </button>
              {checkedOut ? (
                <button className="t-btn t-btn-outline" style={{ minWidth: 130, height: 44, fontSize: 15 }}
                  onClick={undoCheckOut} title="잘못 누른 퇴근을 취소합니다">
                  퇴근 정정
                </button>
              ) : (
                <button className="t-btn t-btn-outline" style={{ minWidth: 130, height: 44, fontSize: 15 }}
                  disabled={!checkedIn} onClick={checkOut}>
                  퇴근 체크
                </button>
              )}
            </div>
            {checkedOut && (
              <div style={{ marginTop: 10, fontSize: 12, color: "var(--text-3)" }}>
                퇴근 {fmtTime(todayRec!.check_out)} · 잘못 눌렀다면 <strong>퇴근 정정</strong>으로 되돌릴 수 있습니다
              </div>
            )}
          </div>

          <div className="t-grid cols-3">
            {([
              { label: "출근 시각", value: fmtTime(todayRec?.check_in ?? null) },
              { label: "퇴근 시각", value: fmtTime(todayRec?.check_out ?? null) },
              { label: checkedOut ? "근무 시간" : "경과 시간", value: checkedIn ? fmtHm(workedMins) : "—" },
            ] as const).map(({ label, value }) => (
              <div key={label} className="t-card" style={{ padding: "14px 18px" }}>
                <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{value}</div>
              </div>
            ))}
          </div>

          {todayRec && todayRec.overtime_minutes > 0 && (
            <div className="t-card" style={{ padding: "12px 18px", display: "flex", alignItems: "center", gap: 8 }}>
              <Pill kind="warn">연장 {fmtHm(todayRec.overtime_minutes)}</Pill>
              <span style={{ fontSize: 12, color: "var(--text-3)" }}>오늘 기준 8시간 초과 근무가 기록되었습니다</span>
            </div>
          )}
        </div>
      )}

      {/* ── 이번 달 ──────────────────────────────────────────────────────── */}
      {items !== null && filter === "month" && (
        <div className="t-grid cols-1" style={{ gap: 16 }}>
          <div className="t-grid cols-3">
            {([
              { label: "출근 일수", value: `${monthStats.days}일` },
              { label: "총 근무", value: fmtHm(monthStats.work) },
              { label: "총 연장", value: fmtHm(monthStats.overtime) },
            ] as const).map(({ label, value }) => (
              <div key={label} className="t-card" style={{ padding: "14px 18px" }}>
                <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{value}</div>
              </div>
            ))}
          </div>
          {items.length === 0 && <Empty msg="근태 기록이 없습니다"/>}
          {items.length > 0 && (
            <div className="t-card" style={{ padding: 0 }}>
              <div className="t-list" style={{ padding: 16 }}>
                {items.map((r) => (
                  <div key={r.id} className="t-row">
                    <span className="meta" style={{ width: 100 }}>{r.work_date}</span>
                    <span className="num">{fmtTime(r.check_in)} → {fmtTime(r.check_out)}</span>
                    <span className="meta">근무 {fmtHm(r.work_minutes)}</span>
                    {r.overtime_minutes > 0 && <Pill kind="warn">연장 {Math.floor(r.overtime_minutes / 60)}h</Pill>}
                    <Pill kind={r.status === "NORMAL" ? "ok" : "muted"}>{r.status ?? "기록"}</Pill>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── 연장근무 ─────────────────────────────────────────────────────── */}
      {items !== null && filter === "overtime" && (
        <div className="t-grid cols-1" style={{ gap: 16 }}>
          <div className="t-card" style={{ padding: "14px 18px", display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 4 }}>이번 달 총 연장근무</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{fmtHm(monthStats.overtime)}</div>
            </div>
            <button className="t-btn t-btn-primary" onClick={() => setReqOpen(true)}>연장근무 신청</button>
          </div>
          {overtimeRecs.length === 0 && <Empty msg="연장근무 기록이 없습니다"/>}
          {overtimeRecs.length > 0 && (
            <div className="t-card" style={{ padding: 0 }}>
              <div className="t-list" style={{ padding: 16 }}>
                {overtimeRecs.map((r) => (
                  <div key={r.id} className="t-row">
                    <span className="meta" style={{ width: 100 }}>{r.work_date}</span>
                    <span className="num">{fmtTime(r.check_in)} → {fmtTime(r.check_out)}</span>
                    <Pill kind="warn">연장 {fmtHm(r.overtime_minutes)}</Pill>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {reqOpen && <OvertimeModal onClose={() => { setReqOpen(false); reload(); }} />}
      {extraReq && <AttendanceRequestModal type={extraReq} onClose={() => setExtraReq(null)} />}
    </>
  );
}

const FORM_TITLES: Record<string, string> = {
  REMOTE_WORK_REQUEST: "재택근무 신청",
  BUSINESS_TRIP_REQUEST: "출장 신청",
  OFF_SITE_REQUEST: "외근 신청",
};

function AttendanceRequestModal({ type, onClose }: { type: string; onClose: () => void }) {
  const [form, setForm] = useState<Record<string, string>>({
    work_date: new Date().toISOString().slice(0, 10),
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date().toISOString().slice(0, 10),
    destination: "",
    location: "",
    purpose: "",
    reason: "",
    tasks: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      // Submit via approval request with correct form code
      await api.post("/approval/documents", {
        form_code: type,
        title: FORM_TITLES[type],
        body_json: JSON.stringify(form),
      });
      toast.success(`${FORM_TITLES[type]} 완료`, "결재 문서가 생성되었습니다");
      onClose();
    } catch (e: any) {
      toast.error("신청 실패", e?.response?.data?.error?.message ?? "");
    } finally {
      setSubmitting(false);
    }
  };

  const f = (key: string, label: string, type_: string = "text") => (
    <div key={key}>
      <label className="t-label">{label}</label>
      <input type={type_} className="t-input" value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
    </div>
  );

  return (
    <div className="t-overlay" onClick={onClose}>
      <div className="t-cmdk" onClick={(e) => e.stopPropagation()} style={{ width: 480 }}>
        <div style={{ padding: 18, borderBottom: "1px solid var(--border)" }}>
          <h3 style={{ margin: 0 }}>{FORM_TITLES[type]}</h3>
        </div>
        <div style={{ padding: 18, display: "grid", gap: 12 }}>
          {type === "REMOTE_WORK_REQUEST" && <>{f("work_date", "재택 근무일", "date")}{f("reason", "사유")}{f("tasks", "업무 계획")}</>}
          {type === "BUSINESS_TRIP_REQUEST" && <>{f("destination", "출장지")}{f("start_date", "출발일", "date")}{f("end_date", "복귀일", "date")}{f("purpose", "출장 목적")}</>}
          {type === "OFF_SITE_REQUEST" && <>{f("location", "외근 장소")}{f("work_date", "외근일", "date")}{f("purpose", "외근 목적")}</>}
        </div>
        <div style={{ padding: 16, borderTop: "1px solid var(--border)", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="t-btn" onClick={onClose}>취소</button>
          <button className="t-btn t-btn-primary" onClick={submit} disabled={submitting}>{submitting ? "신청 중…" : "신청"}</button>
        </div>
      </div>
    </div>
  );
}

function OvertimeModal({ onClose }: { onClose: () => void }) {
  const [form, setForm] = useState({ work_date: new Date().toISOString().slice(0, 10), hours: 2, reason: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/attendance/overtime", form);
      toast.success("연장근무 신청 완료", "결재 문서가 자동 생성되었습니다");
      onClose();
    } catch (e: any) {
      toast.error("신청 실패", e?.response?.data?.error?.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="t-overlay" onClick={onClose}>
      <div className="t-cmdk" onClick={(e) => e.stopPropagation()} style={{ width: 480 }}>
        <div style={{ padding: 18, borderBottom: "1px solid var(--border)" }}>
          <h3 style={{ margin: 0 }}>연장근무 신청</h3>
        </div>
        <div style={{ padding: 18, display: "grid", gap: 12 }}>
          <div><label className="t-label">근무일</label><input type="date" className="t-input" value={form.work_date} onChange={(e) => setForm({ ...form, work_date: e.target.value })}/></div>
          <div><label className="t-label">신청 시간</label><input type="number" min="0.5" max="12" step="0.5" className="t-input" value={form.hours} onChange={(e) => setForm({ ...form, hours: Number(e.target.value) })}/></div>
          <div><label className="t-label">사유</label><textarea className="t-textarea" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}/></div>
        </div>
        <div style={{ padding: 16, borderTop: "1px solid var(--border)", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="t-btn" onClick={onClose}>취소</button>
          <button className="t-btn t-btn-primary" onClick={submit} disabled={submitting}>{submitting ? "신청 중…" : "신청"}</button>
        </div>
      </div>
    </div>
  );
}
